import gql from "graphql-tag";
import { getTokenInfo } from "contexts/AuthManager/Auth";
import { TRACING_HEADERS } from "../constants/constants";
import { DEFAULT_OPTIONS } from "./providerUtils";
import { getShouldAddTracingHeaderToRequests } from "components/Settings/settingsUtils";
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { MIDGARD_CUSTOM_URL_LS_KEY } from "App/CustomAskrUrlIndicator";

const authLink = setContext(() => {
  const { idToken } = getTokenInfo();
  // Return the headers to the context so httpLink can read them
  return {
    headers: {
      Authorization: idToken ? `Bearer ${idToken}` : "no-token",
      "X-Flight-Route": window.location.href,
    },
  };
});

class Clients {
  private clients_ = new Map<string, any>();

  get(ckey: string) {
    const c = this.clients_.get(ckey);
    if (c) {
      return c;
    }

    const cluster = "gke-us-east1";
    const service = "midgard";
    const prodUri = `https://${ckey}.${cluster}.raven-cloud.com/${service}/graphql/`;
    const customMidgardUrl = window.localStorage.getItem(
      MIDGARD_CUSTOM_URL_LS_KEY
    );
    if (customMidgardUrl) {
      console.log("⚠🚨 ~ using custom Midgard url:", customMidgardUrl);
    }
    const isLocalMidgard = ckey && process.env.REACT_APP_LOCAL === "true";
    const uri =
      customMidgardUrl ??
      (isLocalMidgard ? "http://localhost:8080/graphql" : prodUri);

    // https://blog.apollographql.com/batching-client-graphql-queries-a685f5bcd41b
    // link to use if batching (default)
    // also adds a `batch: true` header to the request to prove it's a different link
    const { idToken } = getTokenInfo();

    const shouldAddTracingHeaders = getShouldAddTracingHeaderToRequests();

    // testing: https://dv.midgard.us-east-1.raven-cloud.com/graphiql
    const httpLink = new HttpLink({
      uri,
      headers: {
        Authorization: idToken ? `Bearer ${idToken}` : "no-token",
        ...(shouldAddTracingHeaders ? TRACING_HEADERS : {}),
      },
    });

    const midgardLink = ApolloLink.from([authLink, httpLink]);
    // const batchMidgardLink = ApolloLink.from([
    //   authLink.concat(batchLink)
    // ]);

    this.clients_.set(
      ckey,
      new ApolloClient({
        cache: new InMemoryCache(),
        link: midgardLink,
        defaultOptions: DEFAULT_OPTIONS,
        connectToDevTools: process.env.NODE_ENV === "development",
      })
    );
    // ? Given that we just set this key, can this return undefined?
    return this.clients_.get(ckey);
  }
}

export const midgardClients = new Clients();

// graphiql docs:
// https://chem.gke-us-east1.raven-cloud.com/midgard/graphiql/

// what we can get from the dataDump:
// query DataDump(
//   ge: String!
//   lt: String!
//   cKey: String
//   machemetryIds: [String]
// ) {
//   dataDump(
//     ge: $ge
//     lt: $lt
//     cKey: $cKey
//     machemetryIds: $machemetryIds
//     ): [DataDumpRow]
//   }

// type DataDumpRow = {
//   cKey: String
//   The customer key, i.e., raveng

//   machemetryId: String
//   The machemetry source_base_name of the process

//   processName: String
//   The name of process (the machine or station, e.g., Grinder 1, Line 4)

//   processGroup: String
//   ge: String
//   lt: String
//   asOf: String
//   Time the response was generated, as an epoch milliseconds string.

//   pnlCategory: String
//   tagName: String
//   tagTs: String
//   tagPath: [String]
//   tagId: String
//   teamSize: Int
//   sku: String
//   skuName: String
//   skuScale: Float
//   targetRate: Float
//   rateDenominator: Float
//   actualRate: Float
//   tickDeltaIqm: Float
//   output: Float
//   netOutput: Float
//   grossOutput: Float
//   rejectOutput: Float
//   ticks: Int
//   netTicks: Int
//   grossTicks: Int
//   rejectTicks: Int
//   ticksPort1: Int
//   ticksPort2: Int
//   ticksPort3: Int
//   ticksPort4: Int
//   rejectTicksSpec: Int
//   rejectTicksPseudo: Int
//   rejectTicksFlag: Int
//   operatorId: String
//   shift: String
//   shiftStart: String
//   shiftEnd: String
// }

export const WORK_SCHEDULE_CONFIG_FULL_QUERY = gql`
  query WorkSchedule {
    workScheduleConfigList {
      id
      name
      timeZone
      operational
      startDate
      endDate
      rotationLength
      processes {
        taps {
          name
          machemetryId
        }
        lifecycleStatus
      }
      rotation {
        name
        ordinal
        shiftsCount
        shifts {
          id
          name
          startTime
          endTime
        }
      }
    }
  }
`;

export const SINGLE_SENSE_CONFIG_QUERY = gql`
  query SingleSenseConfig($machemetryId: String!) {
    senseConfig(machemetryId: $machemetryId, serial: "") {
      process {
        playbook {
          skuCode
          plays {
            teamSize
            targetProducingRate {
              intervalMs
              count
            }
          }
        }
      }
    }
  }
`;

export const WORK_SCHEDULE_SHIFT_LIST_FULL_QUERY = gql`
  query WorkScheduleShiftList($id: ID, $ordinal: Int) {
    workScheduleShiftList(id: $id, ordinal: $ordinal) {
      id
      name
      startTime
      endTime
    }
  }
`;

export const WORK_SCHEDULE_DAY_LIST_QUERY = gql`
  query WorkScheduleDayList {
    workScheduleDayList {
      ordinal
      name
      isArchived
      id
      dayOfWeek
      shiftsCount
      shifts {
        endTime
        id
        isArchived
        name
        ordinal
        startTime
      }
      workScheduleConfig {
        timeZone
      }
    }
  }
`;

export const DATA_DUMP_ROWS_QUERY = gql`
  query DataDump(
    $ge: String!
    $lt: String!
    $ckey: String
    $machemetryIds: [String]
    $useCache: Boolean
  ) {
    dataDump(
      ge: $ge
      lt: $lt
      cKey: $ckey
      machemetryIds: $machemetryIds
      useCache: $useCache
    ) {
      ge
      lt
      machemetryId
      operatorId
      pnlCategory
      targetRate
      skuCode
      skuName
      skuStart
      callTarget
      callStart
      output
      netOutput
      grossOutput
      rejectOutput
      targetRate
      teamSize
      skuScale
      rateDenominator
      asOf
      tagName
    }
  }
`;

export const DATA_DUMP_FRAGMENT = gql`
  fragment DataDumpFields on DataDumpRow {
    cKey
    machemetryId
    processName
    processGroup
    callTarget
    callStart
    ge
    lt
    pnlCategory
    tagName
    tagTs
    teamSize
    sku
    skuName
    skuScale
    targetRate
    rateDenominator
    actualRate
    output
    netOutput
    grossOutput
    rejectOutput
    manualRejectOutput
    autoRejectOutput
    ticks
    netTicks
    grossTicks
    rejectTicks
    operatorId
    batchQuantity
    shift
    shiftStart
    shiftEnd
    tickDeltaIqm
    ticksPort1
    ticksPort2
    ticksPort3
    ticksPort4
    tagPath
  }
`;

export const DATA_DUMP_QUERY = gql`
  query DataDump(
    $ge: String!
    $lt: String!
    $cKey: String
    $machemetryIds: [String]
  ) {
    dataDump(ge: $ge, lt: $lt, cKey: $cKey, machemetryIds: $machemetryIds) {
      ...DataDumpFields
    }
  }
  ${DATA_DUMP_FRAGMENT}
`;

// OPEQ = effectiveUptimePercent

// example output: https://bitbucket.org/machinetelemetry/midgard/pull-requests/1295#comment-207773623
export const DATA_DUMP_AGGREGATE_QUERY = gql`
  query DataDumpAggregate(
    $ge: String!
    $lt: String!
    $machemetryIds: [String]
    $shouldAllocate: Boolean
    $useCache: Boolean
  ) {
    dataDumpAggregate(
      ge: $ge
      lt: $lt
      machemetryIds: $machemetryIds
      shouldAllocate: $shouldAllocate
      useCache: $useCache
    ) {
      ge
      lt

      processNames

      producingTime
      speedLossTime
      minorStopTime
      slowdownLossTime
      slowdownLossTimeReal
      rejectLossTime

      productionTime
      plannedTime
      waitingTime
      unplannedTime
      unknownTime
      notScheduledTime
      uncollectedTime

      teepPercent
      teepPercentBottomUp
      availabilityPercent
      performancePercent
      uptimePercent
      effectiveUptimePercent
      effectiveProducingTime
      effectiveProducingTimeTopDown
      effectiveProducingTimeBottomUp

      oeePercent
      oeePercentTopDown
      oeePercentBottomUp

      uslePercent

      unplannedPercent

      qualityPercent

      taggingPercent

      meanTimeBetweenFailures
      meanTimeToRecovery
      avgCycleTime

      netOutput
      grossOutput
      rejectOutput
      netTicks
      grossTicks
      rejectTicks

      ticksPort1
      ticksPort2
      ticksPort3
      ticksPort4

      customMetric01Value
      customMetric02Value
      customMetric03Value
      customMetric04Value
      customMetric05Value
      customMetric06Value
      customMetric07Value
      customMetric08Value

      untaggedPercent
      notScheduledPercent
      plannedDowntimeEfficiency

      outputLossSum
      productionPotentialOutput
      totalPotentialOutput

      operationalAvailabilityPercent
      efficiencyPercent

      pureEquipmentUptimePercent
      uptimePerformancePercent
      producingEfficiencyPercent

      lastSegment {
        ge
        lt
        machemetryId
        operatorId
        pnlCategory
        skuCode
        skuName
        skuStart
        callTarget
        callStart
        output
        netOutput
        grossOutput
        rejectOutput
        targetRate
        teamSize
        skuScale
        rateDenominator
        asOf
        actualRate
      }

      pnlLosses {
        range {
          ge
          lt
        }
        asOf
        totalAllocatedTimeMs
        producing {
          category
          outputLoss
          numberOfEvents
          averageDurationMs
          minorStops {
            numberOfEvents
            averageDurationMs
          }
        }
        planned {
          category
          outputLoss
          numberOfEvents
          averageDurationMs
          downtimePlusDependentSpeedLossMs
          downtimeLossMs
          downtimeDependentSpeedLossMs
          tags {
            name
            numberOfEvents
            averageDurationMs
            path
            downtimePlusDependentSpeedLossMs
            downtimeLossMs
            downtimeDependentSpeedLossMs
            changeoverTime
            outputLoss
          }
        }
        waiting {
          category
          outputLoss
          numberOfEvents
          averageDurationMs
          downtimePlusDependentSpeedLossMs
          downtimeLossMs
          downtimeDependentSpeedLossMs
          tags {
            name
            numberOfEvents
            averageDurationMs
            path
            downtimePlusDependentSpeedLossMs
            downtimeLossMs
            downtimeDependentSpeedLossMs
            changeoverTime
            outputLoss
          }
        }
        unplanned {
          category
          outputLoss
          numberOfEvents
          averageDurationMs
          downtimePlusDependentSpeedLossMs
          downtimeLossMs
          downtimeDependentSpeedLossMs
          tags {
            name
            numberOfEvents
            averageDurationMs
            path
            downtimePlusDependentSpeedLossMs
            downtimeLossMs
            downtimeDependentSpeedLossMs
            changeoverTime
            outputLoss
          }
        }
        notScheduled {
          category
          outputLoss
          numberOfEvents
          averageDurationMs
          downtimePlusDependentSpeedLossMs
          downtimeLossMs
          downtimeDependentSpeedLossMs
          tags {
            name
            numberOfEvents
            averageDurationMs
            path
            downtimePlusDependentSpeedLossMs
            downtimeLossMs
            downtimeDependentSpeedLossMs
            outputLoss
          }
        }
        unknown {
          category
          outputLoss
          numberOfEvents
          averageDurationMs
          downtimePlusDependentSpeedLossMs
          downtimeLossMs
          downtimeDependentSpeedLossMs
          downtimeDependentSlowdownLossMs
          downtimeDependentMinorStopLossMs
          independentSpeedLossMs
          independentSlowdownLossMs
          independentMinorStopLossMs
          speedLossMs
          slowdownLossMs
          minorStopLossMs
          allocatableTimeMs
        }
        quality {
          category
          qualityLossMs
          qualityLossOutput
          scrapLossMs
          scrapLossOutput
          reworkLossMs
          reworkLossOutput
        }
        uncollected {
          category
          totalDurationMs
          numberOfEvents
          averageDurationMs
        }
      }
    }
  }
`;

/** list of tags and number of minutes applied using the supervisor context allocation tool */
export const ALLOCATION_LIST_QUERY = gql`
  query DataDumpAggregate(
    $ge: String!
    $lt: String!
    $machemetryIds: [String]!
  ) {
    allocationList(ge: $ge, lt: $lt, machemetryIds: $machemetryIds) {
      ge
      lt
      machemetryId
      tagName
      category
      numberOfMinutes
      isChangeover
    }
  }
`;

// We only allow 3 levels of subtags right now, so the query only goes 3 levels deep
export const HUBLET_TAGS_QUERY = gql`
  query HubletTags($machemetryId: String!) {
    hubletTags(machemetryId: $machemetryId) {
      tagName
      tagPath
      color
      textColor
      icon
      category
      subTags {
        tagName
        tagPath
        color
        textColor
        icon
        category
        subTags {
          tagName
          tagPath
          color
          textColor
          icon
          category
        }
      }
    }
  }
`;

/** aka Lot Progress query */
export const BATCH_PROGRESSION_QUERY = gql`
  query BatchProgression($machemetryIds: [String]!) {
    batchProgression(machemetryIds: $machemetryIds) {
      ID
      machemetryId
      lotNumber
      lotStartDateMs
      lotSize
      isCompleted
    }
  }
`;

export const TOP_DOWNTIMES_QUERY = gql`
  query TopDowntimes($ge: BigInt!, $machemetryId: String!) {
    topDowntimes(ge: $ge, machemetryId: $machemetryId) {
      ge
      machemetryId
      downtimesWithComment {
        tagName
        problem
        resolution
      }
    }
  }
`;

export const LAST_N_CHANGEOVER_QUERY = gql`
  query LastNChangeovers(
    $lt: String!
    $machemetryIds: [String]
    $numChangeover: Int!
    $intervalLength: Int!
  ) {
    lastNChangeovers(
      lt: $lt
      machemetryIds: $machemetryIds
      numChangeover: $numChangeover
      intervalLength: $intervalLength
    ) {
      tagName
      changeoverDuration
      lossTagName
      lossDuration
      isOngoingChangeover
      range {
        ge
        lt
      }
    }
  }
`;

export const ASKR_CONFIG_QUERY = gql`
  query AskrConfig {
    askrConfig {
      alertRuleConfig
      adlRuleConfig
    }
  }
`;

export const ALERT_RULES_QUERY = gql`
  query AlertRulesQuery {
    askrConfig {
      alertRuleTemplates
    }
  }
`;

export const ADL_RULES_QUERY = gql`
  query ADLRulesQuery {
    askrConfig {
      adlRuleTemplates
    }
  }
`;
